// 一次导航采集一次；不采集页面正文、查询参数或凭证。耗时保留小数。
const round = value => Number.isFinite(value) ? Math.max(0, value) : null;
export const FIRST_SCREEN_REPORT_THRESHOLD_MS = 3000;

export function getLoadingExperience(state) {
  if (!state) return { status: 'unknown', summary: '未采集精简页面展示状态，无法确认是否已降级' };
  const restored = state.fullReadyAtMs !== null && state.fullReadyAtMs !== undefined;
  const summary = state.wasShown
    ? `${state.reason === 'resource-error' ? '资源加载失败' : '加载较慢'}，已展示精简页面（原地址，无整页跳转）${restored ? '；现已恢复完整版' : state.reason === 'resource-error' ? '；等待用户刷新重试' : '；完整版仍在加载'}`
    : state.triggered ? `已触发精简页面，但未确认可见${restored ? '；现已显示完整版' : ''}`
      : restored ? '未展示精简页面，已直接显示完整版' : '尚未展示精简页面，完整版仍在加载';
  return { ...state, summary };
}

export function formatFirstScreenNotification(snapshot) {
  const seconds = ms => `${(round(ms) / 1000).toFixed(2)}秒`;
  const thresholdSeconds = (snapshot.reportThresholdMs ?? FIRST_SCREEN_REPORT_THRESHOLD_MS) / 1000;
  const resourceName = value => {
    if (/galaxy_logo[^/]*\.(?:webp|png)/i.test(value)) return '官网 Logo';
    if (/set_avatar[^/]*\.(?:webp|jpe?g)/i.test(value)) return '数字人产品配图';
    if (/digital-human\/builtin\/thumbnails\//.test(value)) return '客服数字人预览图';
    if (/digital-human\/builtin\/characters\//.test(value)) return '客服数字人视频';
    return value.split('/').pop() || '未知资源';
  };
  const failures = snapshot.resourceErrors || [];
  const codeResources = snapshot.prePaintCodeResources || [];
  const stages = [];
  if (snapshot.navigation?.ttfbMs) stages.push(`等待网页开始返回 ${seconds(snapshot.navigation.ttfbMs)}`);
  if (snapshot.navigation?.htmlDownloadMs) stages.push(`接收HTML ${seconds(snapshot.navigation.htmlDownloadMs)}`);
  if (codeResources.length) stages.push(`首屏程序/样式中最慢一项 ${seconds(Math.max(...codeResources.map(item => item.durationMs)))}`);
  if (snapshot.appMountDurationMs) stages.push(`准备并显示页面 ${seconds(snapshot.appMountDurationMs)}（含等待页面代码）`);
  if (snapshot.blockingMs) stages.push(`浏览器处理任务造成额外阻塞约 ${seconds(snapshot.blockingMs)}`);
  const lines = [
    snapshot.reportTrigger === 'full-page-wait'
      ? `官网完整版加载超过${thresholdSeconds}秒：${seconds(snapshot.fullPageWaitMs)}（首屏绘制 ${seconds(snapshot.firstScreenMs)}）`
      : `官网首屏超过${thresholdSeconds}秒：${seconds(snapshot.firstScreenMs)}`,
    `访问页面：${snapshot.page || '未记录'}`,
    `用户看到什么：${snapshot.fcpMs ? `首次出现内容用时 ${seconds(snapshot.fcpMs)}；` : ''}${snapshot.loadingExperience?.summary || '未采集精简页面展示状态，无法确认是否已降级'}`,
    `访问类型：${/bot|spider|crawl|slurp/i.test(snapshot.userAgent || '') ? '爬虫标识（不能代表普通访客的网速）' : '浏览器访问'}`,
    `主要耗时：${stages.join('；') || '现有记录不足，暂不能判断'}。各阶段可能重叠，不能直接相加。`,
    `诊断线索：${(snapshot.reasons || []).join('；') || '现有记录不足，暂不能判断'}`,
    failures.length
      ? `加载失败：${failures.length}项——${failures.map(item => resourceName(item.url)).join('、')}。当前未记录失败状态码或解码原因，无法确认是网络中断、资源不存在还是设备兼容问题。`
      : '加载失败：未捕获资源加载失败。',
    '以上是观测结果，具体根因尚未确认。',
    '',
    '—— 以下为技术排查明细 ——',
    `观测线索：${(snapshot.reasons || []).join('；')}`,
    JSON.stringify(snapshot, null, 2)
  ];
  return lines.join('\n');
}
const safeUrl = value => { try { const url = new URL(value, location.origin); return url.origin + url.pathname; } catch { return ''; } };
const resourceTiming = (item, paint) => {
  const detail = item.requestStart > 0 && item.responseStart >= item.requestStart;
  const sizes = item.encodedBodySize > 0 && item.decodedBodySize > 0;
  return {
    url: safeUrl(item.name), type: item.initiatorType,
    startMs: round(item.startTime), endMs: round(item.responseEnd), durationMs: round(item.duration),
    transferBytes: item.transferSize || null,
    encodedBodyBytes: item.encodedBodySize || null, decodedBodyBytes: item.decodedBodySize || null,
    protocol: item.nextHopProtocol || 'unknown',
    compression: !sizes ? 'unknown' : item.encodedBodySize < item.decodedBodySize ? 'compressed' : 'identity-size',
    // Fetch-to-request includes connection setup and browser scheduling, not just CDN work.
    fetchToRequestMs: detail && Number.isFinite(item.fetchStart) ? round(item.requestStart - item.fetchStart) : null,
    requestWaitMs: detail ? round(item.responseStart - item.requestStart) : null,
    downloadMs: detail ? round(item.responseEnd - item.responseStart) : null,
    completedBeforePaint: item.responseEnd > 0 && item.responseEnd <= paint,
    timingDetailAvailable: item.responseStart > 0,
    renderBlocking: item.renderBlockingStatus || 'unknown'
  };
};

export function buildFirstScreenReport({ navigation = {}, fcp = 0, lcp = null, resources = [], longTasks = [], errors = [], appStartedAt = 0, appMountedAt = 0, now = 0, network = null }) {
  const duration = lcp?.startTime || fcp || appMountedAt || now;
  const metric = lcp?.startTime ? 'LCP' : fcp ? 'FCP' : 'no-paint-observed';
  const relevant = resources.filter(item => item.startTime <= duration && !/open\.feishu\.cn|\/digital-human(?:-auth)?\//.test(item.name));
  const resourceDetails = relevant.map(item => resourceTiming(item, duration));
  const slowResources = [...resourceDetails].sort((a, b) => b.durationMs - a.durationMs).slice(0, 6);
  // These are candidates, not proof of render blocking. Keep them separate from
  // images/analytics that finish after LCP and used to crowd the top-six report.
  const prePaintCodeResources = resourceDetails.filter(item => item.completedBeforePaint &&
    (item.type === 'script' || /\.css$/.test(item.url)))
    .sort((a, b) => b.durationMs - a.durationMs).slice(0, 6);
  const blockingMs = longTasks.reduce((sum, item) => sum + Math.max(0, Math.min(item.startTime + item.duration, duration) - item.startTime - 50), 0);
  const lcpResource = lcp?.url ? relevant.find(item => item.name === lcp.url) : null;
  const reasons = [];
  const ms = value => `${Math.round(value)}ms`;
  if (network && (/^(slow-)?2g$/.test(network.effectiveType || '') || network.rtt >= 1000 || (network.downlink > 0 && network.downlink < 1))) {
    reasons.push(`浏览器网络估计：${network.effectiveType || '未知类型'}，RTT ${Number.isFinite(network.rtt) ? ms(network.rtt) : '未知'}，下行 ${Number.isFinite(network.downlink) ? network.downlink + 'Mbps' : '未知'}；存在弱网或浏览器限速线索，不能据此确认真实带宽或根因`);
  }
  if (navigation.responseStart > 500) reasons.push(`HTML首字节耗时${round(navigation.responseStart)}ms，需区分网络与服务端响应`);
  const htmlDownloadMs = navigation.responseEnd - navigation.responseStart;
  if (htmlDownloadMs > 500) reasons.push(`HTML接收耗时${ms(htmlDownloadMs)}（首字节${ms(navigation.responseStart)}）；耗时集中在响应体接收阶段，需核对网络限速、服务端分段输出及开发服务状态，不能直接归因后端处理慢`);
  const lateCode = resourceDetails.filter(item => (item.type === 'script' || /\.css$/.test(item.url)) && item.endMs > 1000 && item.durationMs > 200)
    .sort((a, b) => b.durationMs - a.durationMs).slice(0, 3);
  for (const item of lateCode) {
    const phases = item.timingDetailAvailable
      ? `请求前准备${item.fetchToRequestMs === null ? '未知' : ms(item.fetchToRequestMs)}、等待响应${ms(item.requestWaitMs)}、接收响应体${ms(item.downloadMs)}`
      : '分段耗时不可用，无法区分连接、等待响应和下载';
    reasons.push(`脚本/样式 ${item.url} 总耗时${ms(item.durationMs)}：${phases}；${item.completedBeforePaint ? '首屏绘制前完成，但不能单凭完成时间确认阻塞' : '首屏绘制后才完成，不能认定阻塞了本次LCP，需结合完整版就绪时间判断'}${item.encodedBodyBytes === null ? '；资源体积未知，不能用transferBytes推算下载速度或认定缓存命中' : ''}`);
  }
  if (blockingMs > 100) reasons.push(`首屏前主线程长任务阻塞约${round(blockingMs)}ms，可能包含脚本执行和布局`);
  const appMountDurationMs = round(appMountedAt - appStartedAt);
  if (appMountDurationMs > 200) reasons.push(`应用启动到路由就绪并挂载${appMountDurationMs}ms（包含路由分包等待，非纯JS执行）`);
  const lcpPhases = lcpResource ? {
    resourceLoadDelayMs: round(lcpResource.startTime - (navigation.responseStart || 0)),
    resourceLoadMs: round(lcpResource.responseEnd - lcpResource.startTime),
    renderDelayMs: round(duration - lcpResource.responseEnd)
  } : null;
  if (lcpPhases?.resourceLoadMs > 300) reasons.push(`首屏最大内容资源加载${lcpPhases.resourceLoadMs}ms`);
  if (lcpPhases?.renderDelayMs > 300) reasons.push(`最大内容资源完成后到显示仍有${lcpPhases.renderDelayMs}ms`);
  if (errors.length) reasons.push(`捕获${errors.length}个资源加载失败`);
  if (!reasons.length) reasons.push('现有浏览器指标不足以归因，需结合资源明细和性能录制；不能仅凭总耗时判定后端慢');
  return {
    metric, firstScreenMs: round(duration), fcpMs: fcp > 0 ? round(fcp) : null, lcpMs: lcp?.startTime > 0 ? round(lcp.startTime) : null,
    appStartedAtMs: round(appStartedAt), appMountedAtMs: round(appMountedAt), appMountDurationMs,
    navigation: {
      type: navigation.type || 'unknown', redirectMs: round(navigation.redirectEnd - navigation.redirectStart),
      dnsMs: round(navigation.domainLookupEnd - navigation.domainLookupStart),
      connectMs: round(navigation.connectEnd - navigation.connectStart),
      tlsMs: navigation.secureConnectionStart > 0 ? round(navigation.connectEnd - navigation.secureConnectionStart) : null,
      ttfbMs: round(navigation.responseStart), requestWaitMs: round(navigation.responseStart - navigation.requestStart),
      htmlDownloadMs: round(navigation.responseEnd - navigation.responseStart), domReadyMs: round(navigation.domContentLoadedEventEnd), loadMs: round(navigation.loadEventEnd)
    },
    lcpResource: lcp?.url ? safeUrl(lcp.url) : '', lcpElement: lcp?.element?.tagName || '', lcpPhases,
    longTaskCount: longTasks.filter(item => item.startTime < duration).length, blockingMs: round(blockingMs),
    slowResources, prePaintCodeResources,
    resourceErrors: errors.slice(0, 6).map(item => ({ url: safeUrl(item.url), atMs: round(item.atMs), beforePaint: item.atMs <= duration })),
    reasons, rootCauseConfirmed: false,
    timingLimit: '跨域资源未开放Timing-Allow-Origin时无法获取完整分段/大小；原因是观测线索，不等于已确认根因'
  };
}

export function startFirstScreenDiagnostics({ enabled = true, report, getContext = () => ({}) }) {
  if (!enabled || typeof window === 'undefined' || !window.performance || document.visibilityState === 'hidden') return () => {};
  const reportThresholdMs = new URLSearchParams(location.search).get('timeout') === '1' ? 1000 : FIRST_SCREEN_REPORT_THRESHOLD_MS;
  const perf = window.performance, observers = [], errors = [], longTasks = [];
  const initialPage = location.origin + location.pathname;
  const appStartedAt = perf.now();
  let lcp = null, finished = false, settleTimer, deadline, loadingTimer;
  const observe = (type, callback) => {
    if (typeof PerformanceObserver === 'undefined') return;
    try { const observer = new PerformanceObserver(list => callback(list.getEntries())); observer.observe({ type, buffered: true }); observers.push(observer); } catch { /* 旧浏览器降级到FCP和Navigation Timing。 */ }
  };
  observe('largest-contentful-paint', entries => { lcp = entries[entries.length - 1] || lcp; });
  observe('longtask', entries => { longTasks.push(...entries); if (longTasks.length > 100) longTasks.splice(0, longTasks.length - 100); });
  const onError = event => {
    const target = event.target;
    if (target && target !== window && errors.length < 6) errors.push({ url: target.currentSrc || target.src || target.href || '', atMs: perf.now() });
  };
  const cleanup = () => {
    observers.forEach(observer => observer.disconnect()); clearTimeout(settleTimer); clearTimeout(deadline); clearTimeout(loadingTimer);
    window.removeEventListener('load', onLoad); window.removeEventListener('error', onError, true);
    document.removeEventListener('visibilitychange', onVisibility);
    document.removeEventListener('pointerdown', finalize, true); document.removeEventListener('keydown', finalize, true);
    window.removeEventListener('pagehide', finalize);
  };
  const finalize = () => {
    if (finished) return;
    const loadingState = window.__siteLoadingState;
    const fullPageWaitMs = loadingState?.triggered ? round(loadingState.fullReadyAtMs ?? perf.now()) : null;
    // 精简页很早绘制时不能提前停止观察，否则会漏掉完整版持续等待的样本。
    if (loadingState?.triggered && loadingState.fullReadyAtMs == null && fullPageWaitMs <= reportThresholdMs) return;
    finished = true;
    // 回调可能晚于用户交互，先取尚未交付的LCP/长任务记录。
    observers.forEach(observer => {
      for (const entry of observer.takeRecords()) {
        if (entry.entryType === 'largest-contentful-paint') lcp = entry;
        if (entry.entryType === 'longtask') longTasks.push(entry);
      }
    });
    cleanup();
    const connection = navigator.connection;
    const network = connection ? { effectiveType: connection.effectiveType, rtt: connection.rtt, downlink: connection.downlink, saveData: connection.saveData } : null;
    const result = buildFirstScreenReport({ navigation: perf.getEntriesByType('navigation')[0], fcp: perf.getEntriesByName('first-contentful-paint')[0]?.startTime || 0, lcp, resources: perf.getEntriesByType('resource'), longTasks, errors, appStartedAt, appMountedAt: perf.getEntriesByName('galaxy-app-mounted')[0]?.startTime || 0, now: perf.now(), network });
    const slowPaint = result.firstScreenMs > reportThresholdMs;
    const isSlow = slowPaint || fullPageWaitMs > reportThresholdMs;
    // 正常与慢样本全部入库，阈值仅用于标记及飞书告警。
    const snapshot = { ...result, ...getContext(), isSlow, reportTrigger: slowPaint ? 'first-screen' : isSlow ? 'full-page-wait' : 'normal', fullPageWaitMs, reportThresholdMs, loadingExperience: getLoadingExperience(loadingState), page: initialPage, time: new Date().toISOString(), online: navigator.onLine, pageVisibility: document.visibilityState, network, userAgent: navigator.userAgent };
    try { Promise.resolve(report(snapshot)).catch(() => {}); } catch { /* 通知失败不影响网页。 */ }
  };
  const onLoad = () => { clearTimeout(settleTimer); settleTimer = setTimeout(finalize, 1500); };
  const onVisibility = () => { if (document.visibilityState === 'hidden') finalize(); };
  window.addEventListener('error', onError, true);
  window.addEventListener('pagehide', finalize);
  document.addEventListener('visibilitychange', onVisibility);
  document.addEventListener('pointerdown', finalize, true); document.addEventListener('keydown', finalize, true);
  if (document.readyState === 'complete') onLoad(); else window.addEventListener('load', onLoad, { once: true });
  deadline = setTimeout(finalize, 10000);
  loadingTimer = setTimeout(() => {
    if (window.__siteLoadingState?.triggered) finalize();
  }, Math.max(0, reportThresholdMs + 1 - perf.now()));
  return () => { finished = true; cleanup(); };
}
